import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useProductStore } from "stores/useProductStore";

export interface ProductQuickSearchInputProps {
  value: number;
  matchOnSupplier?: boolean; // whether to include supplier code in search
  onChange: (productId: number) => void;
}

export interface ProductQuickSearchInputHandle {
  focus: () => void;
  blur: () => void;
}

export const ProductQuickSearchInput = forwardRef<
  ProductQuickSearchInputHandle,
  ProductQuickSearchInputProps
>(({ value, matchOnSupplier = true, onChange }, ref) => {
  const quickData = useProductStore((s) => s.getActiveProductQuickSearchData());
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(value ? value.toString() : "");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const suppressBlur = useRef(false);

  useImperativeHandle(ref, () => ({
    focus() {
      inputRef.current?.focus();
    },
    blur() {
      inputRef.current?.blur();
    },
  }));

  // Filter matches
  const matches = useMemo(() => {
    const q = text.toLowerCase();
    if (!q) return [];

    // return quickData.filter(
    //   (p) => p.id.toString().startsWith(q) || p.title.toLowerCase().includes(q),
    // );
    return quickData.filter((p) => {
      const idMatch = p.id.toString().startsWith(q);
      const titleMatch = p.title.toLowerCase().includes(q);
      const supplierMatch =
        matchOnSupplier && p.codes
          ? (p.codes ?? "").toLowerCase().includes(q)
          : false;

      return idMatch || titleMatch || supplierMatch;
    });
  }, [text, quickData]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (matches.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, matches.length - 1));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter" || e.key === "Tab") {
      //if (e.key === "Enter") e.preventDefault();

      const selected = matches[highlightIndex];
      if (!selected) return;

      // prevent blur logic
      suppressBlur.current = true;

      // commit selection
      setText(selected.id.toString());
      onChange(Number(selected.id));

      // 👇 CLOSE DROPDOWN IMMEDIATELY
      setOpen(false);

      // 👇 DO NOT blur on Tab — let browser handle it
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
      }
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (text.length > 0) setOpen(true);
        }}
        onBlur={() => {
          if (suppressBlur.current) {
            suppressBlur.current = false;
            return; // skip blur logic entirely
          }
          setOpen(false);

          // Try to resolve the typed value
          const trimmed = text.trim();

          if (!trimmed) return;

          // 1. Exact ID match
          const idMatch = quickData.find((p) => p.id.toString() === trimmed);
          if (idMatch) {
            onChange(Number(idMatch.id));
            inputRef.current?.blur();
            return;
          }

          // 2. Single match by search
          const filtered = quickData.filter(
            (p) =>
              p.id.toString().startsWith(trimmed) ||
              p.title.toLowerCase().includes(trimmed.toLowerCase()),
          );

          if (filtered.length === 1) {
            onChange(Number(filtered[0].id));
            inputRef.current?.blur();
            return;
          }

          // 3. No match → do nothing (or clear if you prefer)
        }}
        onKeyDown={handleKeyDown}
        className="mantine-input"
        style={{ width: "100%", height: 32 }}
      />

      {open && matches.length > 0 && (
        <div
          onMouseDown={(e) => {
            suppressBlur.current = true;
            e.preventDefault();
          }}
          style={{
            position: "absolute",
            top: 34,
            left: 0,
            right: 0,
            width: 550,
            maxHeight: 200,
            overflowY: "auto",
            background: "white",
            border: "1px solid #ccc",
            zIndex: 3000,
            fontSize: 11,
          }}
        >
          {matches.slice(0, 20).map((p, i) => (
            <div
              key={p.id}
              onMouseDown={() => {
                setText(p.id.toString());
                onChange(Number(p.id));
                setOpen(false);
              }}
              style={{
                padding: "4px 8px",
                background: i === highlightIndex ? "#eee" : "white",
                cursor: "pointer",
              }}
            >
              <strong>{p.id}</strong> — {p.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

ProductQuickSearchInput.displayName = "ProductQuickSearchInput";
